import _ from "lodash";
import { DataStorage } from "../../imports";
import { getProductType } from "./threekitCreators";
import {
  getCurrentCanvas,
  getSelectedImage,
} from "../../helpers/fabricHelpers";
import { hasFrontAndBack } from "../../helpers/productHelpers";
// action creators

// Number for fabric movement steps
const STEP = 5;
const ROTATE = 5;

export function saveNode({ data, node }) {
  if (data.configurator) {
    const pointer = DataStorage.create(data.configurator);
    data.configurator = pointer;
  }
  return { type: "player/updateNode", payload: { obj: data, node } };
}

export function getPlayerState(state) {
  let playerState = _.cloneDeep(state.playerState);
  if (playerState.finalObj.nodes) {
    const frames = Object.keys(playerState.finalObj.nodes);
    frames.forEach((frame) => {
      const configuratorPointer =
        playerState.finalObj.nodes[frame].configurator;
      if (configuratorPointer) {
        playerState.finalObj.nodes[frame].configurator =
          DataStorage.find(configuratorPointer);
      }
    });
  }
  return playerState;
}

export function getSelectedNodeData(state) {
  const currentNode = state.playerState.currentNode;
  if (currentNode) {
    const data = { ...state.playerState.finalObj.nodes[currentNode] };
    if (data.configurator) {
      data.configurator = DataStorage.find(data.configurator);
    }
    return data;
  }
  return {};
}

const getControlData = (state) => {
  const { imageMagickObj, finalObj, currentNode } = state.playerState;
  const productType = getProductType(state);
  const nodeData = getSelectedNodeData(state);
  const multipleCanvas = Object.keys(nodeData).length > 0;
  return {
    currentNode,
    imageMagickObj,
    finalObj,
    nodeData,
    multipleCanvas,
    productType,
  };
};

/**
 * Handles imageMagick rotation
 *
 * @param {number}   rotateDirection   1 or -1 to determine what direction object is rotated
 *
 * @returns {Object}
 */
export function rotate(rotateDirection) {
  return (dispatch, getState) => {
    const state = getState();
    const { currentNode, imageMagickObj, nodeData, multipleCanvas } =
      getControlData(state);
    let rotate = multipleCanvas ? nodeData.rotate : imageMagickObj.rotate;
    rotate = rotate + ROTATE * rotateDirection;
    if (rotate >= 360) rotate = 0;
    if (multipleCanvas) {
      dispatch(
        saveNode({
          data: { rotate },
          node: currentNode,
        })
      );
      return;
    } else {
      dispatch({
        type: "player/updatePlayerState",
        payload: { data: { imageMagickObj: { rotate } } },
      });
      return;
    }
  };
}

/**
 * Handles imageMagick horizontal movement
 *
 * @param {number}   moveDirection   1 or -1 to determine what direction object is moved
 *
 * @returns {Object}
 */
export function moveX(moveDirection) {
  return (dispatch, getState) => {
    const state = getState();
    const { currentNode, imageMagickObj, nodeData, multipleCanvas } =
      getControlData(state);
    if (multipleCanvas) {
      let horizontalPos = nodeData.horizontalPos + STEP * moveDirection;
      let horizontalMov = nodeData.horizontalMov - STEP * moveDirection;
      dispatch(
        saveNode({
          data: { horizontalPos, horizontalMov },
          node: currentNode,
        })
      );
      return;
    } else {
      let horizontalPos = imageMagickObj.horizontalPos + STEP * moveDirection;
      let horizontalMov = imageMagickObj.horizontalMov - STEP * moveDirection;
      const newState = {
        imageMagickObj: { horizontalMov, horizontalPos },
      };
      dispatch({
        type: "player/updatePlayerState",
        payload: { data: newState },
      });
      return;
    }
  };
}

/**
 * Handles imageMagick vertical movement
 *
 * @param {number}   moveDirection   1 or -1 to determine what direction object is moved
 *
 * @returns {Object}
 */
export function moveY(moveDirection) {
  return (dispatch, getState) => {
    const state = getState();
    const { currentNode, imageMagickObj, nodeData, multipleCanvas } =
      getControlData(state);
    if (multipleCanvas) {
      let verticalPos = nodeData.verticalPos + STEP * moveDirection;
      let verticalMov = nodeData.verticalMov + STEP * moveDirection;
      dispatch(
        saveNode({
          data: { verticalPos, verticalMov },
          node: currentNode,
        })
      );
      return;
    } else {
      let verticalPos = imageMagickObj.verticalPos + STEP * moveDirection;
      let verticalMov = imageMagickObj.verticalMov + STEP * moveDirection;
      const newState = {
        imageMagickObj: { verticalMov, verticalPos },
      };
      dispatch({
        type: "player/updatePlayerState",
        payload: { data: newState },
      });
      return;
    }
  };
}

/**
 * Handles imageMagick zoom
 *
 * @param {number}   zoomDirection   1 or -1 to determine what direction object is zoomed
 *
 * @returns {Object}
 */
export function zoom(zoomDirection) {
  return (dispatch, getState) => {
    const state = getState();
    const { currentNode, imageMagickObj, finalObj, nodeData, multipleCanvas } =
      getControlData(state);
    let imageHeight, imageWidth, ratio;
    if (multipleCanvas) {
      imageHeight = nodeData.imageHeight;
      imageWidth = nodeData.imageWidth;
      ratio = nodeData.ratio;
    } else {
      imageHeight = imageMagickObj.imageHeight;
      imageWidth = imageMagickObj.imageWidth;
      ratio = finalObj.ratio;
    }

    let change = zoomDirection * 0.017;
    ratio = ratio + change;
    imageHeight = imageHeight + imageHeight * change;
    imageWidth = imageWidth + imageWidth * change;

    if (multipleCanvas) {
      dispatch(
        saveNode({
          data: { ratio, imageHeight, imageWidth },
          node: currentNode,
        })
      );
      return;
    } else {
      const newState = {
        finalObj: { ratio },
        imageMagickObj: {
          imageHeight,
          imageWidth,
        },
      };
      dispatch({
        type: "player/updatePlayerState",
        payload: { data: newState },
      });
    }
  };
}

/**
 * Handles imageMagick color toggle
 *
 * @returns {Object}
 */
export function toggleColor() {
  return (dispatch, getState) => {
    const state = getState();
    const { currentNode, imageMagickObj, nodeData, multipleCanvas } =
      getControlData(state);
    if (multipleCanvas) {
      let blacknwhite = !nodeData.blacknwhite;
      dispatch(
        saveNode({
          data: { blacknwhite },
          node: currentNode,
        })
      );
      return;
    } else {
      dispatch({
        type: "player/updatePlayerState",
        payload: {
          data: {
            imageMagickObj: {
              blacknwhite: !imageMagickObj.blacknwhite,
            },
          },
        },
      });
    }
  };
}

/**
 * Handles builder rotation
 *
 * @param {number}   rotateDirection   1 or -1 to determine what direction object is rotated
 *
 * @returns {Object}
 */
export function fabricRotate(rotateDirection) {
  return (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state);
    const { multipleCanvas, productType } = getControlData(state);
    let activeObject = canvas.getActiveObject() || canvas.item(1) || null;

    if (multipleCanvas && !hasFrontAndBack(productType)) {
      activeObject = getSelectedImage(canvas, state.playerState.currentNode);
    }

    if (activeObject === null) {
      return;
    }

    if (activeObject.name == "imageGroup") {
      for (let i = 0; i < activeObject.getObjects().length; i++) {
        let current = activeObject.getObjects()[i];
        let angle = current.angle + ROTATE * rotateDirection;
        current.set("angle", angle).setCoords;
        canvas.renderAll();
      }
    } else {
      let angle = activeObject.angle + ROTATE * rotateDirection;
      activeObject.set("angle", angle).setCoords();
      canvas.renderAll();
    }

    dispatch(rotate(rotateDirection));
  };
}

/**
 * Handles builder horizontal movement
 *
 * @param {number}   moveDirection   1 or -1 to determine what direction object is moved
 *
 * @returns {Object}
 */
export function fabricMoveX(moveDirection) {
  return (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state);
    const activeObject = canvas.getActiveObject() || canvas.item(1) || null;

    if (activeObject === null) {
      return;
    }

    if (activeObject.name == "imageGroup") {
      for (let i = 0; i < activeObject.getObjects().length; i++) {
        let current = activeObject.getObjects()[i];
        let left = current.left + STEP * moveDirection;
        current.set("left", left).setCoords();
        canvas.renderAll();
      }
    } else {
      let left = activeObject.left + STEP * moveDirection;
      activeObject.set("left", left).setCoords();
      canvas.renderAll();
    }

    dispatch(moveX(moveDirection));
  };
}

/**
 * Handles builder horizontal movement
 *
 * @param {number}   moveDirection   1 or -1 to determine what direction object is moved
 *
 * @returns {Object}
 */
export function fabricMoveY(moveDirection) {
  return (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state);
    const activeObject = canvas.getActiveObject() || canvas.item(1) || null;

    if (activeObject === null) {
      return;
    }

    if (activeObject.name == "imageGroup") {
      for (let i = 0; i < activeObject.getObjects().length; i++) {
        let current = activeObject.getObjects()[i];
        let top = current.top + STEP * moveDirection;
        current.set("top", top).setCoords();
        canvas.renderAll();
      }
    } else {
      let top = activeObject.top + STEP * moveDirection;
      activeObject.set("top", top).setCoords();
      canvas.renderAll();
    }

    dispatch(moveY(moveDirection));
  };
}

/**
 * Handles builder zoom
 *
 * @param {number}   zoomDirection   1 or -1 to determine what direction object is zoomed
 *
 * @returns {Object}
 */
export function fabricZoom(zoomDirection) {
  return (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state);
    const activeObject = (canvas.getActiveObject() || canvas.item(1)) || null;

    if (activeObject === null) {
      return;
    }

    if (activeObject.name == "imageGroup") {
      for (let i = 0; i < activeObject.getObjects().length; i++) {
        let current = activeObject.getObjects()[i];
        let z = current.scaleX + (0.01 * zoomDirection);
        current.scale(z).setCoords();
        canvas.renderAll();
      }
    } else {
      let z = activeObject.scaleX + (0.01 * zoomDirection);
      activeObject.scale(z).setCoords();
      canvas.renderAll();
    }

    dispatch(zoom(zoomDirection));
  };
}

/**
 * Handles builder color toggle
 *
 * @returns {Object}
 */
export function fabricToggleColor() {
  return (dispatch, getState) => {
    const state = getState();
    const canvas = getCurrentCanvas(state);
    const filter = new fabric.Image.filters.Grayscale();
    const activeObject = (canvas.getActiveObject() || canvas.item(1)) || null;

    if (activeObject === null) {
      return;
    }

    if (activeObject.name == "imageGroup") {
      for (let i = 0; i < activeObject.getObjects().length; i++) {
        let current = activeObject.getObjects()[i];
        current.filters.length > 0
          ? current.filters.pop()
          : current.filters.push(filter);
        current.applyFilters();
        canvas.renderAll();
      }
    } else {
      activeObject.filters.length > 0
        ? activeObject.filters.pop()
        : activeObject.filters.push(filter);
      activeObject.applyFilters();
      canvas.renderAll();
    }

    dispatch(toggleColor());
  };
}
